'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCart } from '@/lib/cart-store'
import { BOUTIQUE_EVA_DIAW, SITE_CONFIG } from '@/lib/site-config'
import { toast } from 'sonner'
import {
  ShoppingBag, Trash2, Minus, Plus, Loader2, CheckCircle2,
  X, Send, ArrowRight,
} from 'lucide-react'

export function CartDrawer() {
  const router = useRouter()
  const { items, isOpen, closeCart, updateQuantity, removeItem, clear, getTotalPrice, getTotalItems } = useCart()
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [customer, setCustomer] = useState({
    name: '', email: '', phone: '', message: '',
  })

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  const total = getTotalPrice()
  const count = getTotalItems()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (items.length === 0) {
      toast.error('Votre panier est vide.')
      return
    }
    if (!customer.name || !customer.email) {
      toast.error('Veuillez renseigner votre nom et votre email.')
      return
    }
    setSubmitting(true)
    try {
      const res = await fetch('/api/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer,
          items: items.map(i => ({
            id: i.article.id,
            name: i.article.name,
            rayon: i.article.rayon,
            price: i.article.price,
            unit: i.article.unit,
            quantity: i.quantity,
          })),
          total,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Erreur')
      setSubmitted(true)
      clear()
      toast.success('Commande envoyée !', {
        description: 'Nous vous contacterons sous 24-48h pour finaliser.',
      })
    } catch (e: any) {
      toast.error("Échec de l'envoi", { description: e.message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={(o) => !o && closeCart()}>
      <SheetContent
        side="right"
        className="w-full sm:w-[480px] p-0 bg-background flex flex-col"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-gold/20">
          <SheetTitle className="font-display text-2xl text-cuir flex items-center gap-2">
            <ShoppingBag className="h-6 w-6 text-gold" />
            Mon Panier
            {count > 0 && (
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-cuir text-gold">
                {count}
              </span>
            )}
          </SheetTitle>
          <SheetDescription className="text-foreground/60">
            Sélection {BOUTIQUE_EVA_DIAW.name}
          </SheetDescription>
        </SheetHeader>

        {submitted ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-emerald mb-4" />
            <h3 className="font-display text-2xl text-cuir mb-2">Commande envoyée !</h3>
            <p className="text-foreground/70 mb-6">
              Merci pour votre confiance. Notre équipe va préparer votre commande
              et vous contactera sous 24-48h pour finaliser.
            </p>
            <Button
              variant="outline"
              className="border-gold/40 text-cuir"
              onClick={() => { setSubmitted(false); closeCart() }}
            >
              Continuer mes achats
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <ShoppingBag className="h-16 w-16 text-gold/50 mb-4" />
            <h3 className="font-display text-xl text-cuir mb-2">Panier vide</h3>
            <p className="text-foreground/60 mb-6">
              Parcourez nos rayons et ajoutez vos articles préférés.
            </p>
            <Button
              variant="outline"
              className="border-gold/40 text-cuir hover:bg-gold/10"
              onClick={() => closeCart()}
            >
              Découvrir les rayons
            </Button>
          </div>
        ) : (
          <>
            {/* Items list */}
            <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4 space-y-3">
              {items.map(item => (
                <motion.div
                  key={item.article.id}
                  layout
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="flex gap-3 p-2 rounded-lg border border-gold/15 bg-card"
                >
                  <div className="relative h-16 w-16 rounded-md overflow-hidden flex-shrink-0">
                    <Image
                      src={`/images/products/${item.article.rayon}/${item.article.rayon}-${String(item.article.imageIndex + 1).padStart(3, '0')}.png`}
                      alt={item.article.name}
                      fill
                      sizes="64px"
                      className="object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-cuir line-clamp-2 leading-tight">
                      {item.article.name}
                    </h4>
                    <p className="text-[10px] text-foreground/60 mt-0.5">{item.article.origin}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1 border border-gold/30 rounded-md">
                        <button
                          onClick={() => updateQuantity(item.article.id, item.quantity - 1)}
                          className="h-6 w-6 flex items-center justify-center text-cuir hover:bg-gold/10 rounded-l-md"
                          aria-label="Diminuer"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="text-xs font-medium w-6 text-center text-cuir">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.article.id, item.quantity + 1)}
                          className="h-6 w-6 flex items-center justify-center text-cuir hover:bg-gold/10 rounded-r-md"
                          aria-label="Augmenter"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-display text-sm font-bold text-cuir">
                          {(item.article.price * item.quantity).toFixed(2).replace('.', ',')} €
                        </span>
                        <button
                          onClick={() => removeItem(item.article.id)}
                          className="text-foreground/40 hover:text-destructive transition-colors"
                          aria-label="Retirer"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Customer form */}
              <div className="pt-4 mt-4 border-t border-gold/20 space-y-3">
                <h4 className="font-display text-base text-cuir">Vos coordonnées</h4>
                <div>
                  <Label htmlFor="cust-name" className="text-xs text-cuir">Nom complet *</Label>
                  <Input
                    id="cust-name"
                    value={customer.name}
                    onChange={e => setCustomer({ ...customer, name: e.target.value })}
                    placeholder="Votre nom"
                    className="mt-1 h-9 border-gold/30 focus-visible:ring-gold"
                  />
                </div>
                <div>
                  <Label htmlFor="cust-email" className="text-xs text-cuir">Email *</Label>
                  <Input
                    id="cust-email"
                    type="email"
                    value={customer.email}
                    onChange={e => setCustomer({ ...customer, email: e.target.value })}
                    placeholder="vous@email.com"
                    className="mt-1 h-9 border-gold/30 focus-visible:ring-gold"
                  />
                </div>
                <div>
                  <Label htmlFor="cust-phone" className="text-xs text-cuir">Téléphone</Label>
                  <Input
                    id="cust-phone"
                    value={customer.phone}
                    onChange={e => setCustomer({ ...customer, phone: e.target.value })}
                    placeholder="+33 6 00 00 00 00"
                    className="mt-1 h-9 border-gold/30 focus-visible:ring-gold"
                  />
                </div>
                <div>
                  <Label htmlFor="cust-msg" className="text-xs text-cuir">Message (optionnel)</Label>
                  <Textarea
                    id="cust-msg"
                    value={customer.message}
                    onChange={e => setCustomer({ ...customer, message: e.target.value })}
                    placeholder="Retrait en boutique ou livraison ? Précisions…"
                    rows={2}
                    className="mt-1 border-gold/30 focus-visible:ring-gold resize-none text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gold/30 bg-cuir/5 px-5 py-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground/70">Sous-total</span>
                <span className="font-display text-xl font-bold text-cuir">
                  {total.toFixed(2).replace('.', ',')} €
                </span>
              </div>
              <p className="text-[10px] text-foreground/50">
                Retrait en boutique (Métro Stalingrad) ou livraison sur devis.
                Paiement en magasin.
              </p>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full gradient-emerald-gold border border-gold/50 text-primary-foreground hover:opacity-90 h-11"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Envoi en cours…
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Envoyer ma commande
                  </>
                )}
              </Button>
              <button
                onClick={() => {
                  closeCart()
                  router.push('/panier')
                }}
                className="w-full text-xs text-gold hover:underline transition-colors flex items-center justify-center gap-1.5"
              >
                Voir le panier complet
                <ArrowRight className="h-3 w-3" />
              </button>
              <button
                onClick={clear}
                className="w-full text-xs text-foreground/50 hover:text-destructive transition-colors"
              >
                Vider le panier
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
